import { ImmunizationSearch, SearchStatus } from "../../models/Interfaces";
import React from "react";
import { useTranslation } from "react-i18next";
import "./searchCard.css";
import getIzProvider from "../../models/providers/IzProviders";
import { refreshingRecordsAtom, enqueuedIzSearchAtom } from "../../jotai/atoms";
import { useAtom } from "jotai";
import { getDateTime, getWaitTimeText } from "../../utils/data";
import { assertNever } from "../../utils/assert-never";

interface SearchCardProps {
  index: number;
  search: ImmunizationSearch;
  onViewRecords?: () => void;
  onReviewSearch?: () => void;
  onMoreInfo?: () => void;
  onVerifyContact?: () => void;
}

type StatusColor = "red" | "green" | "yellow" | "purple";

export default function SearchCard({
  search,
  index,
  onViewRecords,
  onReviewSearch,
  onMoreInfo,
  onVerifyContact,
}: SearchCardProps) {
  const { t } = useTranslation();
  const dateVerified = getDateTime(search.dateVerified).isValid;
  const [refreshingRecords] = useAtom(refreshingRecordsAtom);
  const [enqueuedIzSearch] = useAtom(enqueuedIzSearchAtom);
  const izProvider = getIzProvider(search.izProviderKey, t);
  const isVerifiedSearch = () => {
    return (
      search.status.trim() !== SearchStatus.noMatch &&
      search.status.trim() !== SearchStatus.basicMatchNoContacts &&
      search.status.trim() !== SearchStatus.partialMatchNoContacts &&
      dateVerified
    );
  };

  const recordsLoading = refreshingRecords.find(
    (record) => record.attributes.iz_search_uid === search.uid
  );

  const getText = () => {
    const searchStatusToLabel = new Map<string, string>([
      [SearchStatus.inQueue, t("immunizations.search_results_pending")],
      [SearchStatus.match, t("immunizations.search_records_found")],
      [SearchStatus.noMatch, t("immunizations.search_try_again")],
      [SearchStatus.noRecords, t("immunizations.search_try_again")],
      [SearchStatus.multiMatch, t("immunizations.search_results_multi_match")],
      [SearchStatus.partialMatchNoContacts, t("immunizations.search_try_again")],
      [SearchStatus.basicMatchNoContacts, t("immunizations.search_basic_match_no_contacts")],
    ]);

    if (!search.status) {
      return searchStatusToLabel.get(SearchStatus.inQueue);
    }

    const searchStatus = search.status.trim();
    if (searchStatus === SearchStatus.partialMatchAltContacts && dateVerified) {
      // This is effectively a MATCH
      return searchStatusToLabel.get(SearchStatus.match);
    }

    if (searchStatusToLabel.has(searchStatus)) {
      return searchStatusToLabel.get(searchStatus);
    }

    // Un-verified PARTIAL_MATCH_ALT_CONTACTS
    return t("immunizations.search_search_incomplete");
  };

  const getColor = (): StatusColor => {
    let s: StatusColor = "red";
    if (
      search.status.trim() === SearchStatus.noMatch ||
      search.status.trim() === SearchStatus.noRecords ||
      search.status.trim() === SearchStatus.multiMatch ||
      search.status.trim() === SearchStatus.basicMatchNoContacts
    ) {
      s = "red";
    } else if (search.status.trim() !== SearchStatus.noMatch && dateVerified) {
      s = "green";
    } else if (search.status.trim() === SearchStatus.partialMatchAltContacts) {
      s = "yellow";
    } else if (
      (search.status.trim() !== SearchStatus.match && !dateVerified) ||
      (search.status.trim() === SearchStatus.match && recordsLoading)
    ) {
      s = "purple";
    } else {
      if (search.status.trim() === SearchStatus.partialMatchNoContacts) {
        s = "red";
      } else {
        s = "yellow";
      }
    }
    return s;
  };

  const getColorSrc = () => {
    const color = getColor();
    switch (color) {
      case "red":
        return "/assets/images/Icon_Alert.png";
      case "green":
        return "/assets/images/Icon_Check.png";
      case "yellow":
        return "/assets/images/Icon_Confirmation.png";
      case "purple":
        return "/assets/images/Icon_Pending.png";
      default:
        assertNever(color);
    }
  };

  const getColorBgClass = () => {
    const color = getColor();
    switch (color) {
      case "red":
        return "bg-no-match";
      case "green":
        return "bg-match-found";
      case "yellow":
        return "bg-verification-needed";
      case "purple":
        return "bg-records-pending";
      default:
        assertNever(color);
    }
  };

  const name = search.childFirstName
    ? `${search.childFirstName} ${search.childLastName}`
    : `${search.firstName} ${search.lastName}`;

  return (
    <>
      <div
        data-testid=""
        className={`card card-side h-fit ${
          search.status.trim() == SearchStatus.inQueue ? "text-info-content bg-info" : "bg-base-100"
        }`}
      >
        <figure className={`w-12 shrink-0 flex items-center justify-center ${getColorBgClass()}`}>
          <div
            className="w-8 h-8 bg-cover"
            style={{ backgroundImage: `url(${getColorSrc()})` }}
          ></div>
        </figure>
        <div className="card-body">
          <div className="flex flex-wrap items-center">
            <div
              className={`badge h-fit font-semibold ${getColorBgClass()} px-4 dark:text-base-100 dark:border-base-100`}
            >
              <span className="text-base">{getText() + "  "}</span>
            </div>
            {search.status.trim() == SearchStatus.inQueue &&
              enqueuedIzSearch?.uid === search.uid &&
              enqueuedIzSearch?.estimated_wait_time && (
                <strong className="pl-2">
                  {getWaitTimeText(enqueuedIzSearch.estimated_wait_time)}
                </strong>
              )}
          </div>
          <p data-testid="search-card-name" className="font-semibold text-lg">
            {name}
          </p>
          <p className="">
            <strong className="">{izProvider?.jurisdictionAbbrev}</strong>
            <strong className="text-primary">
              {`: ${t("immunizations.search_updated")} ${getDateTime(
                search.dateUpdated
              ).toLocaleString()}`}
            </strong>
          </p>

          <div className="card-actions">
            {onReviewSearch && (
              <button className="btn btn-primary btn-outline" onClick={onReviewSearch}>
                {t("immunizations.no_match_multi_match_review_button")}
              </button>
            )}
            {onViewRecords && (
              <button className="btn btn-primary" onClick={onViewRecords}>
                {t("immunizations.records_found_view_records")}
              </button>
            )}
            {onMoreInfo && (
              <button className="btn btn-primary" onClick={onMoreInfo}>
                {t("immunizations.no_match_multi_match_no_match_button")}
              </button>
            )}
            {onVerifyContact && (
              <button className="btn btn-primary" onClick={onVerifyContact}>
                {t("immunizations.search_search_incomplete")}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
